/**
 * TradeBid brand theme for the OBD-reader admin dashboard.
 *
 * Single source of truth for colours, fonts, and spacing. Every visual
 * decision in this app should reference one of these tokens — if you find
 * yourself reaching for a literal hex value, add it here first.
 *
 * Canonical brand colours are mirrored from the live tradebidauctions.com
 * site + the existing PDF renderer (infra/cdk/lambdas/pdf-renderer/src/Report.tsx).
 * Keeping the three brand tokens (dark / primary / accent) in sync across
 * mobile + dashboard + PDF is what makes the product feel like one product.
 */

const brand = {
  /** Near-black background — tradebidauctions.com hero dark sections. */
  dark: '#04141D',
  /** Cyan primary — tradebidauctions.com primary buttons + accents. */
  primary: '#0FBFFB',
  /** Coral accent — tradebidauctions.com checkmark/highlight accents. */
  accent: '#FD795E',
} as const;

const surface = {
  /** Outermost page background. */
  page: brand.dark,
  /** Top bar / login card. Slightly lighter than `page` so the header
   *  separates visually without a hard border. */
  header: '#0A1F2A',
  /** Panels, table headers, input backgrounds. */
  panel: '#13283A',
  /** Dividers + subtle borders. */
  border: '#1E3A4D',
  /** Input border / button outline. */
  borderStrong: '#284E64',
} as const;

const text = {
  /** Primary text on dark surfaces. */
  primary: '#e2e8f0',
  /** Secondary text (labels, hints). */
  secondary: '#94a3b8',
  /** Tertiary (timestamps, dimmed metadata). */
  tertiary: '#64748b',
  /** Text rendered on top of brand.primary or brand.accent (high-contrast). */
  onBrand: brand.dark,
} as const;

const status = {
  success: '#22c55e',
  warning: '#fbbf24',
  error: '#f87171',
  errorBorder: '#7f1d1d',
} as const;

const radius = {
  sm: 4,
  md: 6,
  lg: 10,
  xl: 14,
} as const;

const fonts = {
  /** Matches the tradebidauctions.com stack. Helvetica Neue ships with
   *  macOS/iOS by default; the system fallback chain matches what the
   *  live site uses on Windows/Linux. */
  sans: '"Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  mono: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
} as const;

export const theme = {
  brand,
  surface,
  text,
  status,
  radius,
  fonts,
} as const;

export type Theme = typeof theme;
